import Info from '@mdi/svg/svg/information.svg?react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import React, { memo } from 'react';
import styled from 'styled-components';
import { COLOR, SIZE } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { Button } from '../Button';
import { FieldInfo } from '../FieldInfo';
import { Flex } from '../Flex';
import { MDIIcon } from '../MDIIcon';
import { type RadioProps } from './Radio.types';

const Radio: React.FC<RadioProps> = memo(
  ({
    handleOnChange,
    disabled = false,
    index,
    value,
    onFocus,
    active = false,
    autoFocus = false,
    checkedValue,
    className = '',
    colorMode = 'light',
    dataTestId = '',
    focus,
    hintText,
    infoIconTooltip,
    labelText,
    name,
    onBlur,
    setupReference,
    toggleRadio,
    withInfoIcon,
  }) => {
    const handleChange = () => {
      if (!disabled) {
        // eslint-disable-next-line
        // @ts-ignore
        handleOnChange?.({ index, value });
      }
    };
    // eslint-disable-next-line
    // @ts-ignore
    const handleFocus = () => onFocus?.({ index, value });

    const isChecked = checkedValue === value;
    const highlightFirst = focus && isNil(checkedValue) && index === 0;
    const isActive = active && (isChecked || highlightFirst);

    const labelClass = classNames(className, { active: isActive }, { disabled });
    const radioClass = classNames('radiomark-wrapper', { checked: isChecked });

    return (
      <Label className={labelClass}>
        <Flex flexDirection="row" alignItems="center">
          <span className={radioClass} data-testid={`${dataTestId}-${value}`}>
            <input
              autoFocus={autoFocus}
              checked={isChecked}
              disabled={disabled}
              name={name}
              onBlur={onBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              onKeyDown={a11yKey(toggleRadio)}
              ref={setupReference}
              type="radio"
              value={value}
            />
            <span className="radiomark" />
          </span>
          {labelText}
          {withInfoIcon && (
            <Button
              className="select-info"
              htmlButtonType="button"
              icon={<MDIIcon icon={Info} className="icon-info" />}
              type="clear"
              iconOnly
              tooltip={infoIconTooltip}
              tooltipStyle={{ maxWidth: '250px' }}
            />
          )}
        </Flex>
        {hintText && <FieldInfo colorMode={colorMode} text={hintText} />}
      </Label>
    );
  },
);

const Label = styled.label`
  .select-info.clear {
    margin: 0;

    .icon-info {
      fill: ${COLOR['light-gray-darkest']};
      width: ${SIZE['16']};
      height: ${SIZE['16']};
      top: 0;
      margin: 0;
    }

    &:hover .icon-info {
      fill: ${COLOR['turquoise-dark']};
    }
  }
`;

export default Radio;
Radio.displayName = 'Radio';
